import { useEffect, useRef, useState } from 'react';
import { isIOS, isMobile } from 'react-device-detect';
import { Sheet } from 'react-modal-sheet';
import { isNativeIOS } from '@pocapan/shared/utils/NativeUtil';
import { useChangeProfileMutation } from '../hooks/useChangeProfileMutation';
import type { UserProfileType } from '../../types/UserType';
import { t } from 'i18next';

interface UpdateUserProfileBottomSheetProps {
  title?: string;
  userProfile?: UserProfileType;
  isShowProfileBottomSheet: boolean;
  setIsShowProfileBottomSheet: (show: boolean) => void;
}

const UpdateUserProfileBottomSheet: React.FC<UpdateUserProfileBottomSheetProps> = ({
  title,
  userProfile,
  isShowProfileBottomSheet,
  setIsShowProfileBottomSheet
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { changeProfileMutation, isLoading } = useChangeProfileMutation();

  const [nickname, setNickname] = useState<string>(userProfile?.nickname || '');

  // 홈 인디케이터 영역 배경색 처리를 위한 CSS
  useEffect(() => {
    const isButtonDisabled = !nickname.trim() || isLoading;
    const lightColor = isButtonDisabled ? '#E7E7E7' : '#5C3DFA';
    const darkColor = isButtonDisabled ? '#303030' : '#5C3DFA';

    const style = document.createElement('style');
    style.textContent = `
      .purple-safe-area-sheet .react-modal-sheet-container::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: env(safe-area-inset-bottom);
        background: ${lightColor};
        z-index: 1;
      }
      .dark .purple-safe-area-sheet .react-modal-sheet-container::after {
        background: ${darkColor};
      }
    `;

    if (isShowProfileBottomSheet) {
      document.head.appendChild(style);
      document.body.classList.add('purple-safe-area-sheet');
    }

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      document.body.classList.remove('purple-safe-area-sheet');
    };
  }, [isShowProfileBottomSheet, nickname, isLoading]);

  const onFocusedInput = (): void => {
    if (isNativeIOS()) return;
    if (containerRef.current && isMobile && isIOS) {
      containerRef.current.style.paddingBottom = '360px';
    }
  };

  const onUnFocusedInput = (): void => {
    if (isNativeIOS()) return;
    if (containerRef.current && isMobile && isIOS) {
      containerRef.current.style.paddingBottom = '0px';
    }
  };

  const onUpdateNickname = (newNickname: string) => {
    setNickname(newNickname);
  };

  const createUserProfile = async (nicknameValue: string) => {
    if (!userProfile?.userProfileId) {
      return;
    }

    changeProfileMutation(
      {
        userProfileId: userProfile.userProfileId,
        nickname: nicknameValue,
        description: userProfile.description || '',
        dataUrl: undefined
      },
      {
        onSuccess: () => {
          setIsShowProfileBottomSheet(false);
        }
      }
    );
  };

  const handleClose = () => {
    setIsShowProfileBottomSheet(false);
    setNickname(userProfile?.nickname || '');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateNickname(e.target.value);
  };

  const handleSubmit = () => {
    if (!isLoading && nickname.trim()) {
      createUserProfile(nickname);
    }
  };

  return (
    <>
      {isShowProfileBottomSheet && (
        <button
          type="button"
          className="fixed inset-0 bg-black bg-opacity-50 z-40 cursor-default"
          onClick={handleClose}
        />
      )}

      <Sheet
        isOpen={isShowProfileBottomSheet}
        disableDrag={true}
        onClose={handleClose}
        detent="content"
        style={{ zIndex: 1000 }}
      >
        <Sheet.Container
          className="!bg-backgroundElevated-light dark:!bg-pure-black relative z-[1000] leading-none"
          style={{
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            left: '50%',
            width: '100%',
            maxWidth: 'calc(var(--chakra-sizes-xl))',
            x: '-50%'
          }}
        >
          <Sheet.Header className="pt-[40px] px-6">
            <div className="text-center">
              <h2 className="text-[17px] font-bold mb-[8px] text-pure-black dark:text-pure-white">
                {t(`myPage.${title}`)}
              </h2>
              <p className="text-[14px] text-main-light-4 dark:text-main-dark-4 font-medium">
                {t('myPage.ProfileNicknameChangeDescription')}
              </p>
            </div>
          </Sheet.Header>

          <Sheet.Content ref={containerRef} className="sticky overflow-x-hidden">
            <div className="mt-[36px] px-[24px]">
              <input
                ref={inputRef}
                type="text"
                placeholder={userProfile?.nickname || ''}
                maxLength={20}
                value={nickname}
                onFocus={onFocusedInput}
                onBlur={onUnFocusedInput}
                onTouchStart={() =>
                  setTimeout(() => {
                    onFocusedInput();
                  }, 150)
                }
                onChange={handleInputChange}
                disabled={isLoading}
                className="w-full px-[16px] py-[17px] rounded-[12px] text-[17px] font-semibold text-main-light-2 bg-secondary-light dark:bg-secondary-dark hover:bg-secondary-light dark:hover:bg-secondary-dark dark:text-main-dark-2 placeholder- dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark border border-transparent disabled:opacity-50 transition-all duration-200"
              />
              <div className="flex justify-between items-center mt-[8px]">
                <div className="ml-auto flex-shrink-0">
                  <span className="text-[13px] text-main-light-4 dark:text-main-dark-4 font-medium">
                    {nickname.length}/20
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              <button
                type="button"
                disabled={!nickname.trim() || isLoading}
                onClick={handleSubmit}
                className={`
                  w-full py-[14px] px-[24px] font-semibold text-[17px]
                  ${
                    !nickname.trim() || isLoading
                      ? 'bg-disabled-light dark:bg-disabled-dark text-onDisabled-light dark:text-onDisabled-dark cursor-not-allowed'
                      : 'bg-primary-light text-onPrimary cursor-pointer'
                  }
                `}
              >
                {t('done')}
              </button>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
    </>
  );
};

export default UpdateUserProfileBottomSheet;
